import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { ChooseLogo } from '@/components/ui/choose-logo';
import {
  ActorRole,
  DataSourceStatus,
  DealState,
  type GetDealsListQuery,
} from '@/lib/graphql/generated/graphql';
import { Check, FileSpreadsheet, Sparkles } from 'lucide-react';

export type DealListRow = GetDealsListQuery['listDeals']['items'][number];

export function getDealListStatus(
  deal: Pick<DealListRow, 'state' | 'editionMode' | 'isOfferControlFlow'>
) {
  if (deal.state === DealState.NegotiationClosed) return 'closed';
  if (deal.isOfferControlFlow) return 'csv';
  if (deal.state === DealState.Empty) return 'notStarted';
  return deal.editionMode === ActorRole.Brand ? 'brand' : 'platform';
}

const IMPORT_RUNNING = new Set<DataSourceStatus>([
  DataSourceStatus.Created,
  DataSourceStatus.Pending,
  DataSourceStatus.Imported,
]);

export function getCsvProgressStatus(
  progress: DealListRow['csvProgress']
):
  | 'csv'
  | 'csvImportFailed'
  | 'csvVerificationFailed'
  | 'csvVerifying'
  | 'csvErrors'
  | 'csvReady' {
  if (!progress) return 'csv';
  const {
    lastImportStatus,
    verificationPending,
    verificationFailed,
    blockingErrorCount,
  } = progress;
  if (lastImportStatus === DataSourceStatus.Error) return 'csvImportFailed';
  if (verificationFailed) return 'csvVerificationFailed';
  if (
    verificationPending ||
    (lastImportStatus && IMPORT_RUNNING.has(lastImportStatus))
  )
    return 'csvVerifying';
  return blockingErrorCount > 0 ? 'csvErrors' : 'csvReady';
}

export function DealStatusBadge({ deal }: { deal: DealListRow }) {
  const { t } = useTranslation();
  const status = getDealListStatus(deal);
  const label =
    status === 'csv'
      ? t(`deals-list.status.${getCsvProgressStatus(deal.csvProgress)}`, {
          count: deal.csvProgress?.blockingErrorCount ?? 0,
        })
      : t(`deals-list.status.${status}`);
  return (
    <Badge
      variant="secondary"
      className="gap-1.5 whitespace-nowrap rounded-sm font-normal"
    >
      {status === 'platform' ? (
        <ChooseLogo width={12} height={12} />
      ) : status === 'csv' ? (
        <FileSpreadsheet size={12} />
      ) : status === 'closed' ? (
        <Check size={12} />
      ) : status === 'notStarted' ? (
        <Sparkles size={12} />
      ) : null}
      {label}
    </Badge>
  );
}
